import { Link, useNavigate } from 'react-router-dom';
import { MarketingHeader } from '../components/MarketingHeader';
import { MarketingFooter } from '../components/MarketingFooter';
import { Shield, ChevronRight } from 'lucide-react';

export function PolitiqueConfidentialitePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MarketingHeader
        onNavigate={(path) => navigate(path)}
        onSignIn={() => navigate('/login')}
        onSignUp={() => navigate('/login')}
      />

      <main className="flex-1">
        <div className="bg-gradient-to-b from-slate-50 to-white border-b border-gray-100 py-12 sm:py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
              <Link to="/" className="hover:text-blue-600 transition-colors">Accueil</Link>
              <ChevronRight size={14} />
              <span className="text-gray-800 font-medium">Politique de confidentialité</span>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Shield size={20} className="text-blue-600" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
                Politique de confidentialité
              </h1>
            </div>
            <p className="text-gray-500 text-sm mt-2">
              Dernière mise à jour : 09/03/2026
            </p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="space-y-10 text-gray-700 leading-relaxed">

            <p>
              La présente politique de confidentialité décrit la manière dont la société <strong>One System</strong> collecte, utilise et protège les données personnelles dans le cadre de l'utilisation du service TrezoOne.
            </p>
            <p>
              TrezoOne est une solution logicielle en ligne permettant aux associations, écoles et organisations de suivre, analyser et anticiper leur trésorerie.
            </p>

            <Section title="1. Responsable du traitement">
              <p>Le responsable du traitement des données est :</p>
              <div className="mt-3 p-4 bg-gray-50 rounded-xl border border-gray-200 text-sm space-y-1">
                <p><strong>One System – SASU</strong></p>
                <p>1484 Route des Gabloteaux, 16130 Juillac-le-Coq</p>
                <p>
                  <a href="mailto:support@trezoone.fr" className="text-blue-600 hover:underline">
                    support@trezoone.fr
                  </a>
                </p>
              </div>
              <p className="mt-3">One System est l'éditeur de la plateforme TrezoOne.</p>
            </Section>

            <Section title="2. Définitions">
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="font-semibold text-gray-800">Donnée personnelle</dt>
                  <dd className="text-gray-600">toute information se rapportant à une personne physique identifiée ou identifiable.</dd>
                </div>
                <div>
                  <dt className="font-semibold text-gray-800">Traitement</dt>
                  <dd className="text-gray-600">toute opération appliquée à des données (collecte, stockage, modification, suppression).</dd>
                </div>
                <div>
                  <dt className="font-semibold text-gray-800">Utilisateur</dt>
                  <dd className="text-gray-600">toute personne utilisant la plateforme TrezoOne.</dd>
                </div>
              </dl>
            </Section>

            <Section title="3. Données collectées">
              <p>Dans le cadre de l'utilisation de TrezoOne, les catégories de données suivantes peuvent être collectées.</p>

              <SubSection title="Données d'identification">
                <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600">
                  <li>nom</li>
                  <li>prénom</li>
                  <li>adresse email</li>
                  <li>fonction au sein de l'organisation</li>
                  <li>nom de la structure</li>
                </ul>
              </SubSection>

              <SubSection title="Données relatives à l'organisation">
                <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600">
                  <li>nom de l'association ou structure</li>
                  <li>informations budgétaires</li>
                  <li>données de trésorerie saisies par l'utilisateur</li>
                  <li>catégories comptables ou budgétaires</li>
                  <li>transactions et prévisions financières</li>
                </ul>
                <p className="text-sm text-gray-500 mt-2">Ces données sont saisies volontairement par les utilisateurs dans l'outil.</p>
              </SubSection>

              <SubSection title="Données techniques">
                <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600">
                  <li>adresse IP</li>
                  <li>type de navigateur</li>
                  <li>système d'exploitation</li>
                  <li>logs techniques de connexion</li>
                  <li>identifiants de session</li>
                </ul>
                <p className="text-sm text-gray-500 mt-2">Ces données sont nécessaires au fonctionnement et à la sécurité du service.</p>
              </SubSection>
            </Section>

            <Section title="4. Finalités du traitement">
              <p>Les données sont collectées uniquement pour les finalités suivantes :</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 mt-3">
                <li>fournir les fonctionnalités de TrezoOne</li>
                <li>permettre le suivi et l'analyse de la trésorerie</li>
                <li>assurer l'accès sécurisé aux comptes utilisateurs</li>
                <li>améliorer les fonctionnalités de la plateforme</li>
                <li>assurer la maintenance et la sécurité du service</li>
                <li>répondre aux demandes des utilisateurs</li>
              </ul>
              <p className="mt-3 text-sm font-medium text-gray-800">Aucune donnée n'est utilisée à des fins de revente ou de profilage commercial.</p>
            </Section>

            <Section title="5. Base légale des traitements">
              <p>Les traitements reposent sur :</p>
              <div className="mt-4 space-y-4">
                <LegalBasis title="l'exécution du contrat" description="lorsque l'utilisateur utilise la plateforme TrezoOne." />
                <LegalBasis title="l'intérêt légitime" description="pour assurer la sécurité, la maintenance et l'amélioration du service." />
                <LegalBasis title="le consentement" description="lorsqu'il est requis, notamment pour certaines fonctionnalités facultatives." />
              </div>
            </Section>

            <Section title="6. Hébergement et localisation des données">
              <p>Les données sont hébergées au sein de l'Union européenne.</p>
              <p className="mt-3">L'infrastructure technique repose notamment sur les services de <strong>Amazon Web Services</strong> situés dans la région West EU (Irlande).</p>
              <p className="mt-3">Les données sont stockées dans des centres de données sécurisés respectant les standards internationaux de sécurité.</p>
              <p className="mt-3">Les données ne sont pas transférées en dehors de l'Union européenne sauf dans les cas suivants :</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 mt-2">
                <li>obligation légale</li>
                <li>nécessité technique encadrée par des garanties conformes au Règlement général sur la protection des données.</li>
              </ul>
            </Section>

            <Section title="7. Sous-traitants">
              <p>Pour assurer le fonctionnement de la plateforme, One System peut faire appel à des prestataires techniques agissant en qualité de sous-traitants.</p>
              <p className="mt-3">Ces prestataires peuvent inclure notamment :</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 mt-2">
                <li>fournisseurs d'infrastructure cloud</li>
                <li>services de gestion de base de données</li>
                <li>outils techniques nécessaires au fonctionnement de la plateforme</li>
              </ul>
              <p className="mt-3">Ces prestataires sont contractuellement tenus de respecter les obligations de sécurité et de confidentialité prévues par le RGPD.</p>
            </Section>

            <Section title="8. Sécurité des données">
              <p>TrezoOne met en œuvre des mesures techniques et organisationnelles destinées à protéger les données personnelles contre tout accès non autorisé, perte, destruction ou altération.</p>
              <p className="mt-3">Ces mesures comprennent notamment :</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 mt-2">
                <li>chiffrement des communications via HTTPS</li>
                <li>sécurisation de l'infrastructure cloud</li>
                <li>gestion stricte des accès aux données</li>
                <li>journalisation des accès et des opérations sensibles</li>
                <li>mises à jour régulières de sécurité</li>
              </ul>
              <p className="mt-3 text-sm text-gray-500 italic">Malgré ces mesures, aucun système informatique ne peut garantir une sécurité absolue.</p>
            </Section>

            <Section title="9. Durée de conservation">
              <p>Les données sont conservées :</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 mt-3">
                <li>pendant toute la durée d'utilisation du service par l'utilisateur</li>
                <li>puis pendant une durée maximale de <strong>3 ans</strong> après la suppression du compte</li>
              </ul>
              <p className="mt-3">Certaines données peuvent être conservées plus longtemps lorsque la loi l'exige.</p>
              <p className="mt-2">Les journaux techniques sont conservés pour une durée limitée à des fins de sécurité.</p>
            </Section>

            <Section title="10. Confidentialité des données financières">
              <p>Les données financières saisies dans TrezoOne (transactions, prévisions de trésorerie, budgets) restent strictement confidentielles.</p>
              <p className="mt-3">One System :</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 mt-2">
                <li>n'exploite pas ces données à des fins commerciales</li>
                <li>ne les revend pas</li>
                <li>ne les analyse pas à des fins de profilage externe</li>
              </ul>
              <p className="mt-3">Ces données sont utilisées uniquement pour fournir les fonctionnalités du service.</p>
            </Section>

            <Section title="11. Droits des utilisateurs">
              <p>Conformément au Règlement général sur la protection des données, les utilisateurs disposent des droits suivants :</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 mt-3">
                <li>droit d'accès</li>
                <li>droit de rectification</li>
                <li>droit d'effacement</li>
                <li>droit à la limitation du traitement</li>
                <li>droit à la portabilité des données</li>
                <li>droit d'opposition</li>
              </ul>
              <p className="mt-4">Toute demande peut être adressée à :</p>
              <div className="mt-2">
                <a href="mailto:support@trezoone.fr" className="text-blue-600 hover:underline font-medium">
                  support@trezoone.fr
                </a>
              </div>
              <p className="mt-4">Les utilisateurs disposent également du droit d'introduire une réclamation auprès de l'autorité de contrôle compétente, notamment la <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">CNIL</a>.</p>
            </Section>

            <Section title="12. Cookies">
              <p>Le site TrezoOne peut utiliser des cookies strictement nécessaires au fonctionnement du service.</p>
              <p className="mt-3">Ces cookies permettent notamment :</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 mt-2">
                <li>la gestion des sessions utilisateurs</li>
                <li>l'amélioration de l'expérience utilisateur</li>
                <li>le fonctionnement technique de la plateforme</li>
              </ul>
              <p className="mt-3">Les utilisateurs peuvent configurer leur navigateur afin de refuser les cookies.</p>
            </Section>

            <Section title="13. Protection des mineurs">
              <p>Le service TrezoOne est destiné à un usage professionnel et n'est pas destiné aux mineurs.</p>
              <p className="mt-3">Les données de mineurs ne sont pas collectées intentionnellement.</p>
            </Section>

            <Section title="14. Modifications de la politique">
              <p>La présente politique de confidentialité peut être mise à jour afin de refléter :</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600 mt-3">
                <li>l'évolution des fonctionnalités du service</li>
                <li>les modifications légales ou réglementaires</li>
              </ul>
              <p className="mt-3">La date de mise à jour est indiquée en haut du document.</p>
            </Section>

            <Section title="15. Contact">
              <p>Pour toute question relative à la protection des données :</p>
              <div className="mt-3">
                <a href="mailto:support@trezoone.fr" className="text-blue-600 hover:underline font-medium">
                  support@trezoone.fr
                </a>
              </div>
            </Section>

          </div>
        </div>
      </main>

      <MarketingFooter />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">
        {title}
      </h2>
      {children}
    </section>
  );
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-5">
      <h3 className="text-sm font-semibold text-gray-800 mb-2">{title}</h3>
      {children}
    </div>
  );
}

function LegalBasis({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
      <div className="flex-1">
        <p className="text-sm font-semibold text-gray-800">{title}</p>
        <p className="text-sm text-gray-600 mt-0.5">{description}</p>
      </div>
    </div>
  );
}
